import type { EntitlementSnapshot } from './entitlements';

export const BILLING_PREVIEW_PAPER_LIMIT = 3;

export interface BillingPreviewState {
    projectId: string | null;
    resultDigestId: string | null;
    resultDeliveredAt: string | null;
    status: string | null;
    requiresCheckout: boolean;
    isActive: boolean;
    isPaused: boolean;
    isAwaitingDigest: boolean;
    isAwaitingDelivery: boolean;
    isDeliveredBeforePause: boolean;
    isResultPreview: boolean;
}

export type BillingPreviewAccessClassification =
    | 'checking'
    | 'unavailable'
    | 'restricted'
    | 'unrestricted';

export interface BillingPreviewClassificationState {
    isPending?: boolean;
    isChecking?: boolean;
    isError?: boolean;
}

export interface BillingPreviewTrialContext {
    projectId?: string | null;
    resultDigestId?: string | null;
}

const AUTHORITATIVE_ENTITLEMENT_SOURCES = new Set([
    'free',
    'subscription',
    'legacy_grace',
    'pre_billing_preview',
]);

function asString(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function asBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') {
        return value;
    }

    if (typeof value === 'number') {
        return value !== 0;
    }

    if (typeof value === 'string') {
        return ['true', 't', '1', 'yes', 'on'].includes(value.trim().toLowerCase());
    }

    return false;
}

function hasAuthoritativeNonPreviewAccess(
    snapshot: EntitlementSnapshot | null | undefined
): boolean {
    const entitlementSource = asString(snapshot?.derived?.entitlement_source);

    return entitlementSource === 'subscription'
        || entitlementSource === 'legacy_grace'
        || asBoolean(snapshot?.derived?.is_friend)
        || asBoolean(snapshot?.derived?.legacy_entitlement_grace)
        || asBoolean(snapshot?.derived?.former_legacy)
        || asBoolean(snapshot?.legacy_transition?.is_former_legacy);
}

/**
 * Reads the durable server-side preview context from the entitlement snapshot.
 * Keep UI surfaces keyed to these IDs so another free or paused project is never
 * mistaken for the one-time signup preview.
 */
export function getBillingPreviewState(
    snapshot: EntitlementSnapshot | null | undefined
): BillingPreviewState {
    const preview = snapshot?.billing_preview;
    const status = asString(preview?.status);
    const requiresCheckout = asBoolean(preview?.requires_checkout);
    const resultDigestId = asString(preview?.result_digest_id);
    const resultDeliveredAt = asString(preview?.result_delivered_at);
    const projectId = asString(preview?.project_id);
    const hasNonPreviewAccess = hasAuthoritativeNonPreviewAccess(snapshot);
    const isActive = !hasNonPreviewAccess
        && (status === 'configured' || status === 'running');
    const isPaused = !hasNonPreviewAccess && status === 'paused';
    const isAwaitingDigest = isActive && !resultDigestId;
    const isAwaitingDelivery = isActive && !resultDeliveredAt;
    const isDeliveredBeforePause = isActive && Boolean(resultDeliveredAt);
    const isResultPreview = Boolean(projectId) && (isActive || isPaused);

    return {
        projectId,
        resultDigestId,
        resultDeliveredAt,
        status,
        requiresCheckout: !hasNonPreviewAccess && requiresCheckout,
        isActive,
        isPaused,
        isAwaitingDigest,
        isAwaitingDelivery,
        isDeliveredBeforePause,
        // The configured project is a restricted one-time preview from the
        // moment it is bound. Digest creation, delivery, and the safe project
        // pause are separate operations and none may expose the full feed.
        isResultPreview,
    };
}

function classifyBillingPreviewAccess(
    snapshot: EntitlementSnapshot | null | undefined,
    matchesPreview: (preview: BillingPreviewState) => boolean,
    state: BillingPreviewClassificationState
): BillingPreviewAccessClassification {
    const preview = getBillingPreviewState(snapshot);
    if (preview.isResultPreview && matchesPreview(preview)) {
        return 'restricted';
    }

    // A retained restricted snapshot remains the safe source of truth while a
    // refresh runs or fails. Any other cached snapshot is treated as stale
    // while checking so it cannot briefly reopen full-feed access.
    // After auth-cache eviction, a pending request can be idle or paused for
    // connectivity. It has not failed merely because it is not fetching yet.
    if (state.isPending || state.isChecking) {
        return 'checking';
    }

    if (state.isError || !snapshot) {
        return 'unavailable';
    }

    // The entitlement endpoint deliberately returns a safe, degraded snapshot
    // with HTTP 200 when its authoritative RPC cannot be trusted. Treat that
    // marker like a failed request so a transport-level success can never open
    // the full feed, digest, or project-paper context.
    const entitlementSource = asString(snapshot.derived?.entitlement_source);
    if (
        asBoolean(snapshot.derived?.entitlement_fallback)
        || !entitlementSource
        || !AUTHORITATIVE_ENTITLEMENT_SOURCES.has(entitlementSource)
    ) {
        return 'unavailable';
    }

    return 'unrestricted';
}

export function classifyBillingPreviewProjectAccess(
    snapshot: EntitlementSnapshot | null | undefined,
    projectId: string | null | undefined,
    state: BillingPreviewClassificationState = {}
): BillingPreviewAccessClassification {
    return classifyBillingPreviewAccess(
        snapshot,
        (preview) => Boolean(projectId) && preview.projectId === projectId,
        state
    );
}

export function classifyBillingPreviewDigestAccess(
    snapshot: EntitlementSnapshot | null | undefined,
    digestId: string | null | undefined,
    state: BillingPreviewClassificationState = {}
): BillingPreviewAccessClassification {
    return classifyBillingPreviewAccess(
        snapshot,
        (preview) => Boolean(digestId) && preview.resultDigestId === digestId,
        state
    );
}

export function isBillingPreviewResultProject(
    snapshot: EntitlementSnapshot | null | undefined,
    projectId: string | null | undefined
): boolean {
    if (!projectId) {
        return false;
    }

    const preview = getBillingPreviewState(snapshot);
    return preview.isResultPreview && preview.projectId === projectId;
}

export function isBillingPreviewResultDigest(
    snapshot: EntitlementSnapshot | null | undefined,
    digestId: string | null | undefined
): boolean {
    if (!digestId) {
        return false;
    }

    const preview = getBillingPreviewState(snapshot);
    return preview.isResultPreview && preview.resultDigestId === digestId;
}

export function isPausedBillingPreviewProject(
    snapshot: EntitlementSnapshot | null | undefined,
    projectId: string | null | undefined
): boolean {
    if (!projectId) {
        return false;
    }

    const preview = getBillingPreviewState(snapshot);
    return preview.isPaused && preview.projectId === projectId;
}

export function isPausedBillingPreviewDigest(
    snapshot: EntitlementSnapshot | null | undefined,
    digestId: string | null | undefined
): boolean {
    if (!digestId) {
        return false;
    }

    const preview = getBillingPreviewState(snapshot);
    return preview.isPaused && preview.resultDigestId === digestId;
}

export function buildBillingPreviewTrialHref(
    context: BillingPreviewTrialContext
): string {
    if (!context.projectId) return '/trial';

    const params = [`preview_project_id=${encodeURIComponent(context.projectId)}`];
    if (context.resultDigestId) {
        params.push(`result_digest_id=${encodeURIComponent(context.resultDigestId)}`);
    }

    return `/trial/preview?${params.join('&')}`;
}

export function capBillingPreviewPapers<T>(papers: readonly T[]): T[] {
    return papers.slice(0, BILLING_PREVIEW_PAPER_LIMIT);
}
